import { cn } from '@utils/index'
import { ReactNode, memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface FurryTableProps {
  className?: {
    table: string
    thead?: string
    th?: string
    tbody?: string
    td?: string
    tr?: string
  }
  rows: ReactNode[][]
  columns: ReactNode[]
  enablePagination?: boolean
  rowsPerPage?: number
}

function FurryTable(props: FurryTableProps) {
  const { enablePagination = false, rowsPerPage = 10 } = props
  const [currentPage, setCurrentPage] = useState(0)
  const totalPages = Math.ceil(props.rows.length / rowsPerPage)
  const { t } = useTranslation()

  const columns = useMemo(
    () =>
      props.columns.map((data, index, array) => (
        <th
          key={index}
          className={cn(
            'p-3 text-sm md:text-base text-left overflow-hidden',
            { 'rounded-tl-lg': index === 0 },
            { 'rounded-tr-lg text-right': index === array.length - 1 },
            props?.className?.th,
          )}
        >
          {data}
        </th>
      )),
    [props.columns],
  )

  const displayedRows = useMemo(() => {
    if (enablePagination) {
      const startIndex = currentPage * rowsPerPage
      const endIndex = startIndex + rowsPerPage
      return props.rows.slice(startIndex, endIndex)
    }
    return props.rows
  }, [props.rows, currentPage, rowsPerPage, enablePagination])

  const rows = useMemo(
    () =>
      displayedRows.map((data, dataIndex, dataArray) => (
        <tr key={dataIndex} className={props?.className?.tr}>
          {data.map((child, childIndex, childArray) => (
            <td
              key={childIndex}
              className={cn(
                'p-3 w-fit text-sm md:text-base',
                {
                  'rounded-bl-lg':
                    dataIndex === dataArray.length - 1 &&
                    childIndex === 0 &&
                    !enablePagination,
                },
                {
                  'rounded-br-lg':
                    dataIndex === dataArray.length - 1 &&
                    childIndex === childArray.length - 1 &&
                    !enablePagination,
                },
                props?.className?.td,
              )}
            >
              {child}
            </td>
          ))}
        </tr>
      )),
    [
      displayedRows,
      props?.className?.td,
      props?.className?.tr,
      enablePagination,
    ],
  )

  const [startIndex, endIndex] = useMemo(() => {
    const start = currentPage * rowsPerPage + 1
    const end = Math.min((currentPage + 1) * rowsPerPage, props.rows.length)
    return [start, end]
  }, [currentPage, rowsPerPage, props.rows.length])

  return (
    <div className="max-w-[100vw]">
      <div className="w-full overflow-x-auto">
        <table
          className={cn(
            'w-full text-white border border-solid border-[#383A3F] rounded-lg border-separate border-spacing-0 bg-dark-6',
            props?.className?.table,
            { 'rounded-b-none': enablePagination },
          )}
        >
          <thead className={`bg-dark-6 ${props?.className?.thead}`}>
            {columns}
          </thead>
          <tbody className={cn('bg-[#1A1B1E]', props?.className?.tbody)}>
            {rows}
          </tbody>
        </table>
      </div>
      {enablePagination && (
        <div className="bg-[#1A1B1E] flex w-full justify-between items-center px-4 py-2 border border-solid border-[#383A3F] rounded-b-lg">
          <p className="text-gray-4">
            {t('table.paginationDescription', {
              startIndex: props.rows.length > 0 ? startIndex : 0,
              endIndex: props.rows.length > 0 ? endIndex : 0,
              length: props.rows.length,
            })}
          </p>
          <div className="flex gap-2">
            <button
              className="btn circle-icon font-bold h-[30px]"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
            >
              ←
            </button>
            <button
              className="btn circle-icon font-bold h-[30px]"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
              }
              disabled={currentPage === totalPages - 1}
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default memo(FurryTable)
